import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { SimpleTabs } from "../../components/simplePage/simpleTabs.tsx";
import { SimpleFieldset } from "../../components/simplePage/simpleFieldset.tsx";
import { useAuth } from "../../auth/authContext.tsx";
import {
  PackageDealMode,
  PackageDealType,
  PackageDealTimeType,
  type PackageDeal,
  type PackageDealTypeArgs,
  type PackageDealGroup,
  type PackageDealTime,
  type PackageDealLocationAccess,
  type ItemList,
  parseTypeArgs,
  getPackageDeal,
  savePackageDeal,
  getItemLists,
  getMembershipName,
  commitPackageDealImage,
} from "../../api/packageDeal.ts";
import { uploadFile } from "../../api/fileUpload.ts";
import { buildAuthHeaders } from "../../api/client.ts";
import type { LocationCacheItem, ProductCacheItem } from "../../types/session.ts";
import { getServerSetting as getServerSettingShared } from "../../util/images.ts";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import { FileInputSafe } from "../../components/ui/fileInputSafe.tsx";
import { ExternalReferenceField } from "./externalReferenceField.tsx";
import { StatusIcon } from "../../components/ui/statusIcon.tsx";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import styles from "../../styles/formPage.module.scss";

/* ── Props ───────────────────────────────────────────────── */

export interface PackageDealEditorProps {
  mode: PackageDealMode;
  dealId: number;
  /** Pre-resolved deal to edit instead of fetching. Wins over the `dealId` fetch, so
   *  a caller that already assembled the deal (Issa chat's "Inspect Deal", which merges
   *  Issa's suggestion into the stored deal) can hand it straight over. Keep `dealId`
   *  set to the deal being updated — the save carries it. Cloned on load, so the
   *  caller's object is never mutated. */
  initialDeal?: PackageDeal;
  /** Field names to flag as changed, for a caller that pre-applied edits to `initialDeal`
   *  (Issa's "Edit" action). Accepted tokens are `PackageDeal` keys — `title`, `type`,
   *  `validStart`, `validEnd`, `groups`, `validDays`, `locationAccess` — and `typeArgs`
   *  keys: `value`, `minReduction`, `maxReduction`, `minAmount`, `maxAmount`, `minCart`,
   *  `maxCart`. Unknown tokens are ignored. Per-group settings (item count, split, free mix)
   *  have no token of their own: they live inside `groups`, which marks the Products tab. */
  highlightFields?: readonly string[];
  /** A generated image (data URI, e.g. from an Issa suggestion) to upload and
   *  set as the deal's kiosk image when the editor opens. The user still
   *  reviews and saves - nothing is persisted to the deal without them. */
  initialImageDataUri?: string;
  /** Called after a successful save — typically closes the dialog + refreshes the grid.
   *  Receives the saved deal's id (useful for a create, where the caller has no id yet). */
  onSaved?: (savedDealId?: number) => void;
  /** Called when the user dismisses the dialog (Cancel, ×, backdrop, Escape). */
  onClose: () => void;
}

/* ── Defaults ────────────────────────────────────────────── */

function defaultDeal(mode: PackageDealMode): PackageDeal {
  const typeMap: Record<PackageDealMode, PackageDealType> = {
    [PackageDealMode.Standard]: PackageDealType.FlatPrice,
    [PackageDealMode.Subsidize]: PackageDealType.Subsidy,
    [PackageDealMode.InteractiveExperience]: PackageDealType.Interactive,
  };
  return {
    id: 0,
    title: "",
    imagePath: "",
    validStart: null,
    validEnd: null,
    bias: 0,
    validDays: [],
    type: typeMap[mode],
    typeArgs:
      mode === PackageDealMode.Standard
        ? { value: 0, minAmount: 0, maxAmount: 9999, minCart: 0, maxCart: 9999 }
        : ([0] as number[]),
    groups: [],
    membership: null,
    operatorId: null,
    locationAccess: null,
    externalReference: "",
    appIconUrl: "",
  };
}

/* ── Time helpers ────────────────────────────────────────── */

const TIME_TYPE_OPTIONS: { value: PackageDealTimeType; label: string }[] = [
  { value: PackageDealTimeType.Sunday, label: "Sunday" },
  { value: PackageDealTimeType.Monday, label: "Monday" },
  { value: PackageDealTimeType.Tuesday, label: "Tuesday" },
  { value: PackageDealTimeType.Wednesday, label: "Wednesday" },
  { value: PackageDealTimeType.Thursday, label: "Thursday" },
  { value: PackageDealTimeType.Friday, label: "Friday" },
  { value: PackageDealTimeType.Saturday, label: "Saturday" },
  { value: PackageDealTimeType.Weekdays, label: "Weekdays" },
  { value: PackageDealTimeType.Weekends, label: "Weekends" },
  { value: PackageDealTimeType.Any, label: "Any" },
];

function formatTimeOfDay(val: string): string {
  try {
    const nums = val.match(/\d{1,2}/g) ?? [];
    const blocks = [
      (nums[0] ?? "00").padStart(2, "0"),
      (nums[1] ?? "00").padStart(2, "0"),
      (nums[2] ?? "00").padStart(2, "0"),
    ];
    if (blocks[0]! > "23") blocks[0] = "23";
    if (blocks[1]! > "59") blocks[1] = "59";
    if (blocks[2]! > "59") blocks[2] = "59";
    return blocks.join(":");
  } catch {
    return "00:00:00";
  }
}

/* ── Group helpers ───────────────────────────────────────── */

/** Tooltip for the Mix column and its checkboxes. */
const FREE_MIX_TITLE =
  "Free mix. Checked: any mix of this group's products fills the item count (2 = A+A or A+B). " +
  "Unchecked: the item count must be the same product (2 = two of one product). Needs 2 or more items.";

/** Free-mix groups above this item count get a save warning: the kiosk search has to try
 *  every way the units can be mixed, and a search that runs too long leaves the cart with no deal. */
const FREE_MIX_WARN_ITEMS = 4;

/** What the cart must hold to satisfy one group — the hint above the group's item picker. */
function groupRuleHint(g: PackageDealGroup, standard: boolean): string {
  const count = g.i ?? 1;
  if (!standard || count <= 1) return "The cart must contain at least one selected item from this group.";
  return g.m
    ? `The cart must contain ${count} selected items from this group, in any mix of the same or different products.`
    : `The cart must contain ${count} of the same selected product from this group.`;
}

/* ── Tree node type ──────────────────────────────────────── */

interface TreeNode {
  id: string;
  label: string;
  itemId: number;
  checked: boolean;
  /** Selected in the deal but not in the lists this session offers (another operator,
   *  outside the user's access, or deleted). Shown so it can be reviewed and unticked. */
  unavailable?: boolean;
}

interface TreeGroup {
  id: string;
  label: string;
  children: TreeNode[];
  tooltip?: string;
  /** Selected items in this group INCLUDING any the render cap left out. */
  selectedCount: number;
  /** Matches hidden by the render cap (never selected ones — those always render). */
  omittedCount: number;
  /** Total matches before the cap. */
  matchCount: number;
  /** Selected ids the list does not offer; counted in selectedCount. */
  unavailableCount: number;
}

/** Render cap per group. Big catalogues (10k+ products) can't all go in the DOM, but
 *  selected items are never subject to it — a checked item you can't see is a trap. */
const TREE_RENDER_CAP = 200;

/** Build a group's visible nodes: every selected match first, then unselected ones up
 *  to the cap. `selectedOnly` narrows it to the current selection for review. */
function buildTreeGroup<T>(
  id: string,
  label: string,
  items: readonly T[],
  toNode: (item: T) => TreeNode,
  selectedOnly: boolean,
  tooltip?: string,
  missing?: { ids: readonly number[]; label: (itemId: number) => string },
): TreeGroup {
  // Ids the deal selects that the list does not offer come first, always: counting them in a
  // tab badge while no row shows them is how a deal ends up saved with items nobody saw.
  const unavailable: TreeNode[] = (missing?.ids ?? []).map((itemId) => ({
    id: `${id}.missing.${itemId}`,
    label: missing!.label(itemId),
    itemId,
    checked: true,
    unavailable: true,
  }));
  const selected: TreeNode[] = [];
  const rest: TreeNode[] = [];
  for (const item of items) {
    const node = toNode(item);
    (node.checked ? selected : rest).push(node);
  }
  const room = Math.max(0, TREE_RENDER_CAP - selected.length);
  const shownRest = selectedOnly ? [] : rest.slice(0, room);
  return {
    id,
    label,
    tooltip,
    children: [...unavailable, ...selected, ...shownRest],
    selectedCount: unavailable.length + selected.length,
    omittedCount: selectedOnly ? 0 : rest.length - shownRest.length,
    matchCount: items.length,
    unavailableCount: unavailable.length,
  };
}

/** Ids a deal record (or id array) selects that are not in `known`. */
function missingSelectedIds(
  r: number[] | Record<number, boolean> | null | undefined,
  known: ReadonlySet<number>,
): number[] {
  if (!r) return [];
  const ids = Array.isArray(r) ? r : Object.entries(r).filter(([, v]) => v).map(([k]) => Number(k));
  return ids.filter((itemId) => Number.isFinite(itemId) && !known.has(itemId));
}

/** Decode a data URI into a File so it can travel the normal upload path.
 *  The extension follows the actual mime type, never an assumed one. */
function dataUriToFile(dataUri: string, baseName: string): File {
  const [meta = "", base64 = ""] = dataUri.split(",");
  const mime = /data:([^;]+)/.exec(meta)?.[1] ?? "image/png";
  const ext = mime === "image/jpeg" ? "jpg" : mime === "image/gif" ? "gif" : "png";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], `${baseName}.${ext}`, { type: mime });
}

/* ── Image item type ─────────────────────────────────────── */

interface ImageItem {
  /** Stored file name (kiosk image) — what goes into the deal's imagePath. */
  name: string;
  /** Display/preview URL (served URL for existing, object URL for a just-uploaded file). */
  url: string;
}

/* ── Image-remove button styles (used by 3 corners) ──────── */

const imageRemoveBtnStyle: React.CSSProperties = {
  position: "absolute",
  top: 4,
  right: 4,
  width: 26,
  height: 26,
  background: "var(--color-bg)",
  border: "1px solid var(--color-border-default)",
  borderRadius: "50%",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  boxShadow: "var(--shadow-sm)",
};

const imageRemoveBtnStyleSmall: React.CSSProperties = {
  ...imageRemoveBtnStyle,
  top: 2,
  right: 2,
  width: 22,
  height: 22,
};

/* ── Component ───────────────────────────────────────────── */

export function PackageDealEditor({ mode, dealId, initialDeal, highlightFields, initialImageDataUri, onSaved, onClose }: PackageDealEditorProps) {
  const { cache } = useAuth();
  const { confirm } = useAlertDialog();
  const [deal, setDeal] = useState<PackageDeal>(() => defaultDeal(mode));
  const [itemLists, setItemLists] = useState<ItemList[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [membershipLabel, setMembershipLabel] = useState("");
  const membershipCacheRef = useRef<Record<string, string>>({});

  // Products tab state
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number>(-1);
  // Images tab state
  const [kioskIcon, setKioskIcon] = useState<ImageItem | null>(null);
  const [kioskThumbs, setKioskThumbs] = useState<ImageItem[]>([]);
  const [appIcon, setAppIcon] = useState<ImageItem | null>(null);
  const [imageBusy, setImageBusy] = useState(false);

  const isNew = !dealId;
  const isIE = mode === PackageDealMode.InteractiveExperience;
  const isSubsidize = mode === PackageDealMode.Subsidize;

  const modeLabel = isIE ? "interactive experience" : isSubsidize ? "subsidy" : "package deal";
  // Free mix is for standard deals only: subsidy group counts already pool across products.
  const isStandard = !isSubsidize && !isIE;
  // ...and only offered once the DB's kiosks can read it (PackageDealFreeMix). With the flag
  // off the column is hidden, but a group that already has "m" keeps it on save and the
  // hint still describes it: hidden, not stripped.
  const freeMixEnabled = useMemo(() => getDbFeatureFlags(cache).PackageDealFreeMix === true, [cache]);
  const showMix = isStandard && freeMixEnabled;

  // ── Load deal + item lists ──────────────────────────────
  // Dedupe by (dealId, mode) — StrictMode double-mount would fire
  // GetPackageDeal + WebGetItemLists twice on first paint.
  const lastLoadKeyRef = useRef<string>("");
  useEffect(() => {
    const key = `${dealId}|${mode}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    setLoading(true);
    setStatusMessage(null);

    const loadDeal = initialDeal
      // Caller supplied the deal — no fetch (deep-cloned below so their object is safe).
      ? Promise.resolve(JSON.parse(JSON.stringify(initialDeal)) as PackageDeal)
      : dealId > 0
      ? getPackageDeal(dealId).then((r) => {
          if (r.oData) return r.oData;
          if (r.bError) throw new Error(r.sMessage || "Failed to load deal");
          return null;
        })
      : Promise.resolve(null);

    const loadLists = getItemLists().then((r) => (r.oData ? r.oData : []));

    Promise.all([loadDeal, loadLists])
      .then(([loadedDeal, lists]) => {
        setItemLists(lists);
        const d = loadedDeal ?? defaultDeal(mode);
        d.groups = (d.groups ?? []).map((g) => ({
          ...g,
          p: arrayToObj(g.p),
          t: arrayToObj(g.t),
          l: arrayToObj(g.l),
        }));
        setDeal(d);
        loadImages(d);
        if (initialImageDataUri && !generatedImageAppliedRef.current) {
          generatedImageAppliedRef.current = true;
          void attachGeneratedImage(initialImageDataUri, d.operatorId ?? null);
        }
      })
      .catch((err) => {
        setStatusMessage({ type: "error", message: String(err) });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dealId, mode]);

  function arrayToObj(arr: number[] | Record<number, boolean>): Record<number, boolean> {
    if (!arr) return {};
    if (!Array.isArray(arr)) return arr;
    const obj: Record<number, boolean> = {};
    for (const id of arr) obj[id] = true;
    return obj;
  }

  function objToArray(obj: number[] | Record<number, boolean>): number[] {
    if (!obj) return [];
    if (Array.isArray(obj)) return obj;
    return Object.keys(obj).filter((k) => obj[k as unknown as number]).map(Number);
  }

  // Attach a generated image handed in by the caller (an Issa suggestion):
  // it travels the exact same upload+commit path as a hand-picked file and
  // becomes the kiosk image, demoting any existing one to the thumbnails.
  // Invoked from the load pipeline (never an effect) so it always runs with
  // the LOADED deal - committing under the right operator, after loadImages
  // has populated the current icon state.
  const kioskIconRef = useRef<ImageItem | null>(null);
  useEffect(() => { kioskIconRef.current = kioskIcon; }, [kioskIcon]);
  const generatedImageAppliedRef = useRef(false);

  async function attachGeneratedImage(dataUri: string, operatorId: number | null) {
    setImageBusy(true);
    try {
      const file = dataUriToFile(dataUri, `issa_deal_${Date.now()}`);
      const headers = await buildAuthHeaders();
      const up = await uploadFile(file, headers, { purpose: "packagedeal-image" });
      const res = await commitPackageDealImage(up.StoredPath, makeUniqueName(file), operatorId, false);
      if (res.bError || !res.oData) {
        setStatusMessage({ type: "error", message: res.sMessage || "Couldn't attach the generated image." });
        return;
      }
      const item: ImageItem = { name: res.oData, url: URL.createObjectURL(file) };
      const previous = kioskIconRef.current;
      if (previous) setKioskThumbs((thumbs) => [previous, ...thumbs]);
      setKioskIcon(item);
    } catch (err) {
      setStatusMessage({ type: "error", message: String(err) });
    } finally {
      setImageBusy(false);
    }
  }

  // ── Image loading ─────────────────────────────────────
  function loadImages(d: PackageDeal) {
    const segments = (d.imagePath || "")
      .split("|")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const imgUrl = getServerSetting("ImageURL")?.replace(/\/+$/, "") ?? "";
    const kioskBase = d.operatorId ? `${imgUrl}/f/${d.operatorId}/` : `${imgUrl}/f/`;
    const rootUrl = imgUrl.replace(/\/pictures\/?$/i, "");
    const appBase = `${rootUrl}/app/`;

    if (segments.length > 0) {
      setKioskIcon({ name: segments[0]!, url: `${kioskBase}${segments[0]}` });
      setKioskThumbs(
        segments.slice(1).map((s) => ({ name: s, url: `${kioskBase}${s}` })),
      );
    } else {
      setKioskIcon(null);
      setKioskThumbs([]);
    }

    if (d.appIconUrl && d.appIconUrl.trim()) {
      const isData = d.appIconUrl.startsWith("data:");
      const name = isData ? `app_${Date.now()}` : d.appIconUrl.substring(d.appIconUrl.lastIndexOf("/") + 1);
      const url = isData || d.appIconUrl.startsWith(appBase) ? d.appIconUrl : `${appBase}${name}`;
      setAppIcon({ name, url });
    } else {
      setAppIcon(null);
    }
  }

  function getServerSetting(key: string): string | undefined {
    return getServerSettingShared(cache, key);
  }

  // ── Membership lookup ─────────────────────────────────
  const lookupMembership = useCallback(
    (guid: string) => {
      if (!guid) {
        setMembershipLabel("open");
        return;
      }
      if (membershipCacheRef.current[guid]) {
        setMembershipLabel(membershipCacheRef.current[guid]);
        return;
      }
      setMembershipLabel("loading...");
      getMembershipName(guid).then((r) => {
        if (r.oData) {
          membershipCacheRef.current[guid] = r.oData;
          setMembershipLabel(r.oData);
        } else {
          setMembershipLabel("not found");
        }
      });
    },
    [],
  );

  useEffect(() => {
    lookupMembership(deal.membership || "");
  }, [deal.membership, lookupMembership]);

  // ── Field updater ─────────────────────────────────────
  const updateDeal = useCallback(
    <K extends keyof PackageDeal>(key: K, value: PackageDeal[K]) => {
      setDeal((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const typeArgs = useMemo(() => parseTypeArgs(deal.typeArgs), [deal.typeArgs]);

  const updateTypeArgs = useCallback(
    (key: keyof PackageDealTypeArgs, val: string) => {
      const parsed = val.trim() === "" ? null : parseFloat(val);
      setDeal((prev) => {
        const ta = parseTypeArgs(prev.typeArgs);
        return { ...prev, typeArgs: { ...ta, [key]: parsed } };
      });
    },
    [],
  );

  // ── Item lists by type ────────────────────────────────
  const prodLists = useMemo(() => itemLists.filter((l) => l.type?.toUpperCase().trim() === "PROD"), [itemLists]);
  const locLists = useMemo(() => itemLists.filter((l) => l.type?.toUpperCase().trim() === "LOC"), [itemLists]);

  // ── Products, PriceTypes from cache ───────────────────
  const products = useMemo<ProductCacheItem[]>(() => {
    if (!cache?.CommonData?.Product) return [];
    return cache.CommonData.Product as unknown as ProductCacheItem[];
  }, [cache]);

  const priceTypes = useMemo(() => {
    if (!cache?.CommonData?.PriceType) return [];
    return cache.CommonData.PriceType as unknown as { PriceTypeID: number; TypeName: string }[];
  }, [cache]);

  const locations = useMemo<LocationCacheItem[]>(() => {
    if (!cache?.CommonData?.Location) return [];
    return cache.CommonData.Location as unknown as LocationCacheItem[];
  }, [cache]);

  // What this session's lists offer (scoped to the user's operators and access), to spot
  // selected ids from elsewhere, such as an Issa suggestion made from wider data.
  const productIds = useMemo(() => new Set(products.map((p) => p.ProductID)), [products]);
  const prodListIds = useMemo(() => new Set(prodLists.map((l) => l.id)), [prodLists]);
  const priceTypeIds = useMemo(() => new Set(priceTypes.map((t) => t.PriceTypeID)), [priceTypes]);
  const locationIds = useMemo(() => new Set(locations.map((l) => l.LocationID)), [locations]);
  const locListIds = useMemo(() => new Set(locLists.map((l) => l.id)), [locLists]);

  // ── Type options by mode ──────────────────────────────
  const typeOptions = useMemo(() => {
    switch (mode) {
      case PackageDealMode.InteractiveExperience:
        return [{ value: PackageDealType.Interactive, label: "Interactive Experience" }];
      case PackageDealMode.Subsidize:
        return [{ value: PackageDealType.Subsidy, label: "Subsidize" }];
      default:
        return [
          { value: PackageDealType.Percent, label: "Percent" },
          { value: PackageDealType.FlatReduction, label: "Flat reduction" },
          { value: PackageDealType.FlatPrice, label: "Flat price" },
        ];
    }
  }, [mode]);

  // ── Groups management ─────────────────────────────────
  const addGroup = useCallback(() => {
    setDeal((prev) => {
      const newGroup: PackageDealGroup = {
        n: `New group ${prev.groups.length + 1}`,
        i: isSubsidize || isIE ? null : 1,
        p: {},
        t: {},
        l: {},
        s: null,
      };
      const groups = [...prev.groups, newGroup];
      setSelectedGroupIndex(groups.length - 1);
      return { ...prev, groups };
    });
  }, [isSubsidize, isIE]);

  const removeGroup = useCallback(() => {
    if (selectedGroupIndex < 0) return;
    setDeal((prev) => {
      const groups = prev.groups.filter((_, i) => i !== selectedGroupIndex);
      setSelectedGroupIndex(groups.length > 0 ? Math.min(selectedGroupIndex, groups.length - 1) : -1);
      return { ...prev, groups };
    });
  }, [selectedGroupIndex]);

  const updateGroup = useCallback(
    (index: number, key: keyof PackageDealGroup, val: unknown) => {
      setDeal((prev) => {
        const groups = [...prev.groups];
        groups[index] = { ...groups[index]!, [key]: val } as PackageDealGroup;
        return { ...prev, groups };
      });
    },
    [],
  );

  // ── Times management ──────────────────────────────────
  const addTimeRule = useCallback(() => {
    setDeal((prev) => ({
      ...prev,
      validDays: [
        ...prev.validDays,
        { d: PackageDealTimeType.Any, s: "00:00:00", e: "23:59:59" },
      ],
    }));
  }, []);

  const removeTimeRule = useCallback(
    (index: number) => {
      setDeal((prev) => ({
        ...prev,
        validDays: prev.validDays.filter((_, i) => i !== index),
      }));
    },
    [],
  );

  const updateTimeRule = useCallback(
    (index: number, key: keyof PackageDealTime, val: string | number, normalize = true) => {
      setDeal((prev) => {
        const validDays = [...prev.validDays];
        const rule: PackageDealTime = { ...validDays[index]! };
        if (key === "d") {
          rule.d = Number(val) as PackageDealTimeType;
        } else {
          // Keep the raw text while typing; only normalize to HH:MM:SS when the field loses focus.
          rule[key] = normalize ? formatTimeOfDay(String(val)) : String(val);
        }
        validDays[index] = rule;
        return { ...prev, validDays };
      });
    },
    [],
  );

  // ── Save ──────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setStatusMessage(null);

    // Validate amounts for Standard mode
    if (mode === PackageDealMode.Standard) {
      const ta = parseTypeArgs(deal.typeArgs);
      if (ta.minAmount == null || ta.maxAmount == null || ta.minCart == null || ta.maxCart == null) {
        setStatusMessage({ type: "error", message: "Fill in Minimal amount, Maximal amount, Min cart amount and Max cart amount." });
        return;
      }
    }

    // Warnings
    const warnings: string[] = [];
    if (!isIE) {
      // The kiosk walks the deal's groups to find what to reduce: a group without items covers every
      // product, but no group at all covers nothing, so the two cases get different warnings.
      const noGroup = !deal.groups || deal.groups.length === 0;
      const noProduct = !noGroup &&
        !deal.groups.some((g) => objToArray(g.p).length > 0 || objToArray(g.l).length > 0 || objToArray(g.t).length > 0);
      if (!deal.locationAccess || (!deal.locationAccess.lists && !deal.locationAccess.locations)) {
        warnings.push("No location limitation — will be available at all locations.");
      }
      if (noGroup) {
        warnings.push("No product group — the kiosk cannot apply this deal to anything. Add a group on the Products tab; a group without selected items covers every product.");
      } else if (noProduct) {
        warnings.push("No product limitation — will be available for any product.");
      }
      const ta = parseTypeArgs(deal.typeArgs);
      if (mode === PackageDealMode.Standard && (!ta.maxAmount)) {
        warnings.push("Maximal amount is 0 — will not be able to trigger on kiosk.");
      }
      const bigMix = isSubsidize ? [] : deal.groups.filter((g) => g.m && (g.i ?? 1) > FREE_MIX_WARN_ITEMS);
      if (bigMix.length > 0) {
        warnings.push(
          `Free mix of more than ${FREE_MIX_WARN_ITEMS} items (${bigMix.map((g) => `"${g.n}"`).join(", ")}) — ` +
          "over large product lists the kiosk deal search can get slow, and on the default (V1) search a cart can time out and get no deals at all.",
        );
      }
    }

    if (warnings.length > 0) {
      const proceed = await confirm({
        title: "Continue with warnings?",
        body: warnings.join("\n\n"),
        confirmLabel: "Continue",
      });
      if (!proceed) return;
    }

    // Build save payload
    const out: PackageDeal = {
      ...deal,
      typeArgs: parseTypeArgs(deal.typeArgs),
      groups: deal.groups.map((g) => {
        const i = (isSubsidize || isIE) && g.i == null ? -1 : (g.i ?? 1);
        const group: Record<string, unknown> = { ...g, i };
        // Free mix only means something for a standard group of 2+ units, so "m" is written
        // only then — every other group keeps exactly the JSON older editors produced.
        if (g.m && i > 1 && !isSubsidize && !isIE) group.m = true; else delete group.m;
        // Backend treats a present-but-empty "p"/"t"/"l" array as a real
        // (invalid) selection for Subsidy/IE deals, so omit empty ones
        // rather than sending [] (matches WebAdmin2's Fill()).
        const p = objToArray(g.p);
        const t = objToArray(g.t);
        const l = objToArray(g.l);
        if (p.length > 0) group.p = p; else delete group.p;
        if (t.length > 0) group.t = t; else delete group.t;
        if (l.length > 0) group.l = l; else delete group.l;
        return group as unknown as PackageDealGroup;
      }),
    };

    // Build locationAccess from state
    // already in deal

    setSaving(true);
    try {
      const result = await savePackageDeal(out);
      if (result.bError || !result.oData) {
        setStatusMessage({ type: "error", message: result.sMessage || "Failed to save." });
        return;
      }
      // Re-fetch canonical state
      const savedId = result.oData.id;
      const refetch = await getPackageDeal(savedId);
      if (refetch.oData) {
        const d = refetch.oData;
        d.groups = (d.groups || []).map((g) => ({
          ...g,
          p: arrayToObj(g.p),
          t: arrayToObj(g.t),
          l: arrayToObj(g.l),
        }));
        setDeal(d);
        loadImages(d);
      }
      setStatusMessage({ type: "success", message: "Saved successfully." });
      onSaved?.(savedId);
    } catch (err) {
      setStatusMessage({ type: "error", message: String(err) });
    } finally {
      setSaving(false);
    }
  }, [deal, mode, isIE, isSubsidize, onSaved, confirm]);

  // ── Image upload helpers ──────────────────────────────
  // Upload the file to the user store, then commit it into package-deal picture storage
  // (validated server-side). We keep only the returned reference (name / URL) — no base64
  // is ever embedded in the deal.
  function makeUniqueName(file: File, prefix = ""): string {
    const sanitized = file.name.replace(/[^\w.\-]+/g, "");
    const ext = sanitized.split(".").pop() ?? "";
    const base = sanitized.substring(0, sanitized.length - ext.length - 1);
    return `${prefix}${base}_${Date.now()}.${ext}`;
  }

  async function handleKioskUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImageBusy(true);
    setStatusMessage(null);
    try {
      const headers = await buildAuthHeaders();
      const up = await uploadFile(file, headers, { purpose: "packagedeal-image" });
      const res = await commitPackageDealImage(up.StoredPath, makeUniqueName(file), deal.operatorId ?? null, false);
      if (res.bError || !res.oData) {
        setStatusMessage({ type: "error", message: res.sMessage || "Couldn't upload the image." });
        return;
      }
      // res.oData = stored file name; preview from a local object URL.
      setKioskThumbs((prev) => [...prev, { name: res.oData!, url: URL.createObjectURL(file) }]);
    } catch (err) {
      setStatusMessage({ type: "error", message: String(err) });
    } finally {
      setImageBusy(false);
    }
  }

  async function handleAppUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImageBusy(true);
    setStatusMessage(null);
    try {
      const headers = await buildAuthHeaders();
      const up = await uploadFile(file, headers, { purpose: "packagedeal-image" });
      const res = await commitPackageDealImage(up.StoredPath, makeUniqueName(file, "app_"), deal.operatorId ?? null, true);
      if (res.bError || !res.oData) {
        setStatusMessage({ type: "error", message: res.sMessage || "Couldn't upload the image." });
        return;
      }
      // res.oData = public app-icon URL; that's both the preview and what's saved.
      setAppIcon({ name: res.oData!, url: res.oData! });
    } catch (err) {
      setStatusMessage({ type: "error", message: String(err) });
    } finally {
      setImageBusy(false);
    }
  }

  function setAsKioskIcon(item: ImageItem) {
    const oldIcon = kioskIcon;
    setKioskIcon(item);
    setKioskThumbs((prev) => {
      const filtered = prev.filter((t) => t.name !== item.name);
      if (oldIcon) filtered.unshift(oldIcon);
      return filtered;
    });
  }

  async function removeKioskIcon() {
    const ok = await confirm({ title: "Remove the kiosk image?", confirmLabel: "Remove", destructive: true });
    if (!ok) return;
    setKioskIcon(null);
  }

  async function removeKioskThumb(name: string) {
    const ok = await confirm({ title: "Remove this thumbnail?", confirmLabel: "Remove", destructive: true });
    if (!ok) return;
    setKioskThumbs((prev) => prev.filter((t) => t.name !== name));
  }

  async function removeAppIcon() {
    const ok = await confirm({ title: "Remove the app image?", confirmLabel: "Remove", destructive: true });
    if (!ok) return;
    setAppIcon(null);
  }

  // ── Sync images into deal before save ─────────────────
  // We need to build imagePath + appIconUrl from image state
  useEffect(() => {
    const imgUrl = getServerSetting("ImageURL")?.replace(/\/+$/, "") ?? "";
    const kioskBase = deal.operatorId ? `${imgUrl}/f/${deal.operatorId}/` : `${imgUrl}/f/`;
    // Images are already committed to storage (upload + commit on select), so the deal
    // carries only the stored name — never inline base64.
    function pieceOf(item: ImageItem): string {
      return item.name;
    }

    const parts: string[] = [];
    const seenNames: string[] = [];

    if (kioskIcon) {
      parts.push(pieceOf(kioskIcon));
      seenNames.push(kioskIcon.name);
    }
    for (const t of kioskThumbs) {
      if (!seenNames.includes(t.name)) {
        parts.push(pieceOf(t));
        seenNames.push(t.name);
      }
    }

    const newImagePath = parts.join("|");
    const newFullIconPath = kioskIcon ? `${kioskBase}${kioskIcon.name}` : "";

    const newAppIconUrl = appIcon ? appIcon.url : "";

    setDeal((prev) => {
      if (prev.imagePath === newImagePath && prev.fullIconImagePath === newFullIconPath && prev.appIconUrl === newAppIconUrl) {
        return prev;
      }
      return { ...prev, imagePath: newImagePath, fullIconImagePath: newFullIconPath, appIconUrl: newAppIconUrl };
    });
  }, [kioskIcon, kioskThumbs, appIcon, deal.operatorId]);

  // ── Location checkbox handling ────────────────────────
  const toggleLocationAccess = useCallback(
    (group: "lists" | "locations", id: number, checked: boolean) => {
      setDeal((prev) => {
        const la: PackageDealLocationAccess = {
          lists: { ...(prev.locationAccess?.lists ?? {}) },
          locations: { ...(prev.locationAccess?.locations ?? {}) },
        };
        if (checked) {
          la[group]![id] = true;
        } else {
          delete la[group]![id];
        }
        return { ...prev, locationAccess: la };
      });
    },
    [],
  );

  // ── Product checkbox handling ─────────────────────────
  const toggleGroupItem = useCallback(
    (groupIndex: number, itemGroup: "p" | "t" | "l", itemId: number, checked: boolean) => {
      setDeal((prev) => {
        const groups = [...prev.groups];
        const g = { ...groups[groupIndex] };
        const obj = { ...(g[itemGroup] as Record<number, boolean>) };
        if (checked) {
          obj[itemId] = true;
        } else {
          delete obj[itemId];
        }
        g[itemGroup] = obj;
        groups[groupIndex] = g as PackageDealGroup;
        return { ...prev, groups };
      });
    },
    [],
  );

  // ── Changed-field highlighting ─────────────────────────
  // A caller may hand over a deal with edits already applied (Issa's "Edit" action);
  // these helpers mark the affected rows and tabs so the change is visible at a glance.
  const changed = useMemo(() => new Set(highlightFields ?? []), [highlightFields]);
  /** Row class — adds the "changed" accent when any of the given fields was edited. */
  const rowClass = useCallback(
    (...fields: string[]) => {
      const base = styles.fieldRowStacked;
      return fields.some((f) => changed.has(f)) ? `${base} ${styles.fieldChanged}` : base;
    },
    [changed],
  );
  /** Marker appended to a tab label when something inside that tab was edited. */
  const tabLabel = useCallback(
    (label: string, ...fields: string[]) =>
      fields.some((f) => changed.has(f)) ? (
        <>
          {label}
          <span className={styles.tabChangedDot} aria-label="changed" />
        </>
      ) : (
        label
      ),
    [changed],
  );

  // ── Tab: Deal/Info ────────────────────────────────────
  const dealTab = (
    <div className={styles.formContainer}>
      <div className={rowClass("title")}>
        <label>Title</label>
        <input
          className={styles.fieldInput}
          value={deal.title}
          onChange={(e) => updateDeal("title", e.target.value)}
        />
      </div>

      <div className={rowClass("validStart", "validEnd")}>
        <label>{isIE ? "Start" : "Deal Start"}</label>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <input
            type="datetime-local"
            className={styles.fieldInput}
            style={{ minWidth: 0 }}
            value={deal.validStart ? deal.validStart.substring(0, 16) : ""}
            onChange={(e) => updateDeal("validStart", e.target.value || null)}
          />
          <input
            type="datetime-local"
            className={styles.fieldInput}
            style={{ minWidth: 0 }}
            value={deal.validEnd ? deal.validEnd.substring(0, 16) : ""}
            onChange={(e) => updateDeal("validEnd", e.target.value || null)}
            placeholder="End"
          />
        </div>
        <span className={styles.hint}>
          Leave start or end empty for the {modeLabel} to have no start or end date.
        </span>
      </div>

      {!isIE && (
        <>
          <div className={rowClass("type")}>
            <label>Deal type</label>
            <select
              className={styles.fieldSelect}
              value={deal.type}
              onChange={(e) => updateDeal("type", Number(e.target.value) as PackageDealType)}
            >
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className={rowClass("value")}>
            <label>Price reduction</label>
            <input
              className={styles.fieldInput}
              type="number"
              step="any"
              value={typeArgs.value ?? ""}
              onChange={(e) => updateTypeArgs("value", e.target.value)}
            />
          </div>

          {deal.type === PackageDealType.Percent && !isSubsidize && (
            <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
              <div className={rowClass("minReduction")} style={{ flex: 1 }}>
                <label>At least</label>
                <input
                  className={styles.fieldInput}
                  type="number"
                  step="any"
                  value={typeArgs.minReduction ?? ""}
                  onChange={(e) => updateTypeArgs("minReduction", e.target.value)}
                />
              </div>
              <div className={rowClass("maxReduction")} style={{ flex: 1 }}>
                <label>Up to</label>
                <input
                  className={styles.fieldInput}
                  type="number"
                  step="any"
                  value={typeArgs.maxReduction ?? ""}
                  onChange={(e) => updateTypeArgs("maxReduction", e.target.value)}
                />
              </div>
            </div>
          )}

          {mode === PackageDealMode.Standard && (
            <>
              <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
                <div className={rowClass("minAmount")} style={{ flex: 1 }}>
                  <label>Minimal amount</label>
                  <input
                    className={styles.fieldInput}
                    type="number"
                    step="any"
                    value={typeArgs.minAmount ?? ""}
                    onChange={(e) => updateTypeArgs("minAmount", e.target.value)}
                  />
                </div>
                <div className={rowClass("maxAmount")} style={{ flex: 1 }}>
                  <label>Maximal amount</label>
                  <input
                    className={styles.fieldInput}
                    type="number"
                    step="any"
                    value={typeArgs.maxAmount ?? ""}
                    onChange={(e) => updateTypeArgs("maxAmount", e.target.value)}
                  />
                </div>
              </div>
              <span className={styles.hint}>
                The minimal and maximal total base price of items to be selected. Set 0 / 9999 to allow any price.
              </span>

              <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
                <div className={rowClass("minCart")} style={{ flex: 1 }}>
                  <label>Min cart amount</label>
                  <input
                    className={styles.fieldInput}
                    type="number"
                    step="any"
                    value={typeArgs.minCart ?? ""}
                    onChange={(e) => updateTypeArgs("minCart", e.target.value)}
                  />
                </div>
                <div className={rowClass("maxCart")} style={{ flex: 1 }}>
                  <label>Max cart amount</label>
                  <input
                    className={styles.fieldInput}
                    type="number"
                    step="any"
                    value={typeArgs.maxCart ?? ""}
                    onChange={(e) => updateTypeArgs("maxCart", e.target.value)}
                  />
                </div>
              </div>
              <span className={styles.hint}>
                Cart value limits for deal eligibility. Set 0 / 9999 to allow any.
              </span>
            </>
          )}

          {!isIE && (
            <div className={styles.fieldRowStacked}>
              <label>
                Membership{membershipLabel ? <em style={{ marginLeft: 8, fontWeight: "normal" }}>{membershipLabel}</em> : ""}
              </label>
              <input
                className={styles.fieldInput}
                value={deal.membership || ""}
                onChange={(e) => updateDeal("membership", e.target.value || null)}
                placeholder="GUID (leave empty for open)"
              />
            </div>
          )}
        </>
      )}

      {isIE ? (
        <div className={styles.fieldRowStacked}>
          <label>URL</label>
          <input
            className={styles.fieldInput}
            value={deal.externalReference || ""}
            onChange={(e) => updateDeal("externalReference", e.target.value)}
          />
        </div>
      ) : (
        <ExternalReferenceField
          value={deal.externalReference || ""}
          onChange={(v) => updateDeal("externalReference", v)}
        />
      )}
    </div>
  );

  // ── Tab: Locations ────────────────────────────────────
  const [locFilter, setLocFilter] = useState("");
  const [locSelectedOnly, setLocSelectedOnly] = useState(false);
  const locFilterRe = useMemo(() => {
    try { return new RegExp(locFilter, "i"); } catch { return /./i; }
  }, [locFilter]);

  const locationGroups = useMemo<TreeGroup[]>(() => {
    const groups: TreeGroup[] = [];
    const la = deal.locationAccess ?? { lists: null, locations: null };

    if (!isSubsidize) {
      groups.push(buildTreeGroup(
        "locations",
        "Location",
        locations.filter((l) => locFilterRe.test(l.StoreName || "") || locFilterRe.test(l.City || "")),
        (l) => ({
          id: `locations.${l.LocationID}`,
          label: `${l.StoreName}${l.City ? ` — ${l.City}` : ""}`,
          itemId: l.LocationID,
          checked: !!la.locations?.[l.LocationID],
        }),
        locSelectedOnly,
        undefined,
        { ids: missingSelectedIds(la.locations, locationIds), label: (itemId) => `Location #${itemId}` },
      ));
    }

    groups.push(buildTreeGroup(
      "lists",
      "Location group",
      locLists.filter((l) => locFilterRe.test(l.name || "")),
      (l) => ({
        id: `lists.${l.id}`,
        label: l.name || `List #${l.id}`,
        itemId: l.id,
        checked: !!la.lists?.[l.id],
      }),
      locSelectedOnly,
      undefined,
      { ids: missingSelectedIds(la.lists, locListIds), label: (itemId) => `Location group #${itemId}` },
    ));

    return groups;
  }, [locations, locLists, deal.locationAccess, isSubsidize, locFilterRe, locSelectedOnly, locationIds, locListIds]);

  /** Total locations + location groups picked, for the tab badge and the summary line. */
  const locationSelectedTotal = useMemo(() => {
    const la = deal.locationAccess ?? { lists: null, locations: null };
    const count = (r?: Record<number, boolean> | null) =>
      r ? Object.values(r).filter(Boolean).length : 0;
    return count(la.locations) + count(la.lists);
  }, [deal.locationAccess]);

  const locationsTab = (
    <div>
      <div style={{ marginBottom: "var(--space-2)" }}>
        <input
          className={styles.fieldInput}
          placeholder="Filter..."
          value={locFilter}
          onChange={(e) => setLocFilter(e.target.value)}
        />
      </div>
      <div className={styles.treeToolbar}>
        <span className={styles.hint} style={{ margin: 0 }}>
          {isSubsidize
            ? "Which location groups the subsidy should be active. Marking none makes it active on all."
            : "Which locations and/or location groups the deal should be active. Marking none makes it active on all."}
        </span>
        <label className={styles.checkLabel} style={{ flexShrink: 0 }}>
          <input
            type="checkbox"
            checked={locSelectedOnly}
            onChange={(e) => setLocSelectedOnly(e.target.checked)}
            disabled={locationSelectedTotal === 0}
          />
          Show selected only ({locationSelectedTotal})
        </label>
      </div>
      {locationGroups.map((group) => (
        <CheckboxTree
          key={group.id}
          group={group}
          onToggle={(itemId, checked) =>
            toggleLocationAccess(group.id as "lists" | "locations", itemId, checked)
          }
        />
      ))}
    </div>
  );

  // ── Tab: Products ─────────────────────────────────────
  const [prodFilter, setProdFilter] = useState("");
  const [prodSelectedOnly, setProdSelectedOnly] = useState(false);
  const prodFilterRe = useMemo(() => {
    try { return new RegExp(prodFilter, "i"); } catch { return /./i; }
  }, [prodFilter]);

  const selectedGroup = selectedGroupIndex >= 0 ? deal.groups[selectedGroupIndex] : null;

  const productGroups = useMemo<TreeGroup[]>(() => {
    if (!selectedGroup) return [];
    const groups: TreeGroup[] = [];
    const gp = selectedGroup.p as Record<number, boolean>;
    const gt = selectedGroup.t as Record<number, boolean>;
    const gl = selectedGroup.l as Record<number, boolean>;

    if (!isSubsidize) {
      groups.push(buildTreeGroup(
        "p",
        "Product",
        products.filter((p) => prodFilterRe.test(p.Name || "") || prodFilterRe.test(String(p.ProductID))),
        (p) => ({
          id: `p.${p.ProductID}`,
          label: `${p.ProductID}, ${p.Name}`,
          itemId: p.ProductID,
          checked: !!gp[p.ProductID],
        }),
        prodSelectedOnly,
        "Format: ProductID, Product Name",
        { ids: missingSelectedIds(selectedGroup.p, productIds), label: (itemId) => `${itemId}, product` },
      ));
    }

    groups.push(buildTreeGroup(
      "l",
      "Product list",
      prodLists.filter((l) => prodFilterRe.test(l.name || "")),
      (l) => ({
        id: `l.${l.id}`,
        label: l.name || `List #${l.id}`,
        itemId: l.id,
        checked: !!gl[l.id],
      }),
      prodSelectedOnly,
      undefined,
      { ids: missingSelectedIds(selectedGroup.l, prodListIds), label: (itemId) => `Product list #${itemId}` },
    ));

    groups.push(buildTreeGroup(
      "t",
      "Product type",
      priceTypes.filter((t) => prodFilterRe.test(t.TypeName || "")),
      (t) => ({
        id: `t.${t.PriceTypeID}`,
        label: t.TypeName,
        itemId: t.PriceTypeID,
        checked: !!gt[t.PriceTypeID],
      }),
      prodSelectedOnly,
      undefined,
      { ids: missingSelectedIds(selectedGroup.t, priceTypeIds), label: (itemId) => `Product type #${itemId}` },
    ));

    return groups;
  }, [selectedGroup, products, prodLists, priceTypes, isSubsidize, prodFilterRe, prodSelectedOnly, productIds, prodListIds, priceTypeIds]);

  /** Items picked in the group currently being edited. */
  const groupSelectedCount = useMemo(
    () => productGroups.reduce((sum, g) => sum + g.selectedCount, 0),
    [productGroups],
  );

  /** Items picked across every group — drives the Products tab badge. */
  const productSelectedTotal = useMemo(() => {
    const count = (r: number[] | Record<number, boolean> | undefined) =>
      !r ? 0 : Array.isArray(r) ? r.length : Object.values(r).filter(Boolean).length;
    return (deal.groups ?? []).reduce((sum, g) => sum + count(g.p) + count(g.t) + count(g.l), 0);
  }, [deal.groups]);

  const productsTab = (
    <div>
      <SimpleFieldset legend="Groups">
        <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
          <button className={styles.secondaryBtn} onClick={addGroup}>Add group</button>
          <button className={styles.secondaryBtn} onClick={removeGroup} disabled={selectedGroupIndex < 0}>
            Remove group
          </button>
        </div>
        <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th} aria-label="Select" style={{ width: 32 }}></th>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>Items</th>
              {showMix && <th className={styles.th} title={FREE_MIX_TITLE}>Mix</th>}
              {!isSubsidize && <th className={styles.th}>Split</th>}
            </tr>
          </thead>
          <tbody>
            {deal.groups.map((g, i) => (
              <tr
                key={i}
                onClick={() => setSelectedGroupIndex(i)}
                style={{
                  cursor: "pointer",
                  background: i === selectedGroupIndex ? "var(--color-bg-sidebar)" : undefined,
                }}
              >
                <td className={styles.td} style={{ textAlign: "center" }}>
                  <input
                    type="radio"
                    name="pkgGroupSel"
                    aria-label={`Select group ${g.n || i + 1}`}
                    checked={i === selectedGroupIndex}
                    onChange={() => setSelectedGroupIndex(i)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className={styles.td}>
                  <input
                    className={styles.fieldInput}
                    value={g.n}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateGroup(i, "n", e.target.value)}
                    style={{ maxWidth: 150 }}
                  />
                </td>
                <td className={styles.td}>
                  <input
                    className={styles.fieldInput}
                    type="number"
                    value={g.i ?? ""}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const val = e.target.value.trim() === "" ? null : parseInt(e.target.value, 10) || (isSubsidize || isIE ? null : 1);
                      updateGroup(i, "i", val);
                    }}
                    style={{ maxWidth: 60 }}
                  />
                </td>
                {showMix && (
                  // Tooltip on the cell, so it also shows while the checkbox is disabled.
                  <td className={styles.td} style={{ textAlign: "center" }} title={FREE_MIX_TITLE}>
                    <input
                      type="checkbox"
                      aria-label={`Free mix for group ${g.n || i + 1}`}
                      checked={!!g.m && (g.i ?? 1) > 1}
                      disabled={(g.i ?? 1) <= 1}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateGroup(i, "m", e.target.checked)}
                    />
                  </td>
                )}
                {!isSubsidize && (
                  <td className={styles.td}>
                    <input
                      className={styles.fieldInput}
                      type="number"
                      value={g.s ?? ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const val = e.target.value.trim() === "" ? null : parseInt(e.target.value, 10) || null;
                        updateGroup(i, "s", val);
                      }}
                      style={{ maxWidth: 60 }}
                    />
                  </td>
                )}
              </tr>
            ))}
            {deal.groups.length === 0 && (
              <tr><td className={styles.emptyRow} colSpan={3 + (showMix ? 1 : 0) + (isSubsidize ? 0 : 1)}>No groups</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </SimpleFieldset>

      {selectedGroup && (
        <SimpleFieldset legend={`Items for "${selectedGroup.n}"`}>
          <div style={{ marginBottom: "var(--space-2)" }}>
            <input
              className={styles.fieldInput}
              placeholder="Filter products, lists and types…"
              value={prodFilter}
              onChange={(e) => setProdFilter(e.target.value)}
            />
          </div>
          <div className={styles.treeToolbar}>
            <span className={styles.hint} style={{ margin: 0 }}>
              {groupRuleHint(selectedGroup, isStandard)}
            </span>
            <label className={styles.checkLabel} style={{ flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={prodSelectedOnly}
                onChange={(e) => setProdSelectedOnly(e.target.checked)}
                disabled={groupSelectedCount === 0}
              />
              Show selected only ({groupSelectedCount})
            </label>
          </div>
          {productGroups.map((group) => (
            <CheckboxTree
              key={group.id}
              group={group}
              onToggle={(itemId, checked) => {
                if (itemId === -1) return; // omit placeholder
                toggleGroupItem(selectedGroupIndex, group.id as "p" | "t" | "l", itemId, checked);
              }}
            />
          ))}
        </SimpleFieldset>
      )}
    </div>
  );

  // ── Tab: Valid Times ──────────────────────────────────
  const timesTab = (
    <div>
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap", marginBottom: "var(--space-2)" }}>
        <label style={{ fontWeight: "var(--font-semibold)" }}>Bias</label>
        <input
          className={styles.fieldInput}
          type="number"
          style={{ flex: "0 0 auto", width: 80, minWidth: 80 }}
          value={deal.bias}
          onChange={(e) => updateDeal("bias", parseInt(e.target.value, 10) || 0)}
        />
        <span className={styles.hint}>
          Allow the {modeLabel} to be available *bias* seconds before and after the below time spans.
        </span>
      </div>
      <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
        <button className={styles.secondaryBtn} onClick={addTimeRule}>Add row</button>
      </div>
      <span className={styles.hint}>
        The {modeLabel} will apply if the list below is empty, or if any rule matches the session start time.
      </span>
      <div className={styles.tableScroll}>
      <table className={styles.table} style={{ marginTop: "var(--space-2)" }}>
        <thead>
          <tr>
            <th className={styles.th}>Valid</th>
            <th className={styles.th}>Start</th>
            <th className={styles.th}>End</th>
            <th className={styles.th} style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {deal.validDays.map((rule, i) => (
            <tr key={i}>
              <td className={styles.td}>
                <select
                  className={styles.fieldSelect}
                  value={rule.d}
                  onChange={(e) => updateTimeRule(i, "d", e.target.value)}
                >
                  {TIME_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </td>
              <td className={styles.td}>
                <input
                  className={styles.fieldInput}
                  value={rule.s}
                  placeholder="HH:MM:SS"
                  onChange={(e) => updateTimeRule(i, "s", e.target.value, false)}
                  onBlur={(e) => updateTimeRule(i, "s", e.target.value)}
                  style={{ maxWidth: 100 }}
                />
              </td>
              <td className={styles.td}>
                <input
                  className={styles.fieldInput}
                  value={rule.e}
                  placeholder="HH:MM:SS"
                  onChange={(e) => updateTimeRule(i, "e", e.target.value, false)}
                  onBlur={(e) => updateTimeRule(i, "e", e.target.value)}
                  style={{ maxWidth: 100 }}
                />
              </td>
              <td className={styles.td}>
                <button className={styles.dangerBtn} onClick={() => removeTimeRule(i)} style={{ padding: "2px 8px" }}>
                  &times;
                </button>
              </td>
            </tr>
          ))}
          {deal.validDays.length === 0 && (
            <tr><td className={styles.emptyRow} colSpan={4}>No time rules — always active</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );

  // ── Tab: Images ───────────────────────────────────────
  const imagesTab = (
    <div>
      <SimpleFieldset legend={isIE ? "Main Image" : "Kiosk Image"}>
        {!isIE && (
          <span className={styles.hint}>
            The icon shown in the cart. Click a thumbnail to swap it as the active kiosk icon.
          </span>
        )}
        <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start", marginTop: "var(--space-2)" }}>
          <div style={{ width: 180, maxWidth: "100%", minHeight: 108, border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-1)", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--color-bg)", position: "relative" }}>
            {kioskIcon ? (
              <>
                <img src={kioskIcon.url} alt={kioskIcon.name} style={{ maxWidth: "100%", maxHeight: 108, objectFit: "contain" }} />
                <button
                  type="button"
                  onClick={removeKioskIcon}
                  aria-label="Remove kiosk image"
                  title="Remove kiosk image"
                  style={imageRemoveBtnStyle}
                >
                  <StatusIcon kind="cogDelete" size={14} style={{ color: "var(--color-error)" }} />
                </button>
              </>
            ) : (
              <span style={{ color: "var(--color-text-muted)", fontSize: "var(--text-xs)" }}>No image</span>
            )}
          </div>
          <label className={styles.secondaryBtn} style={{ cursor: imageBusy ? "default" : "pointer", opacity: imageBusy ? 0.6 : 1 }}>
            {imageBusy ? "Uploading…" : "Add kiosk image"}
            <FileInputSafe accept="image/png,image/gif,image/jpeg" hidden disabled={imageBusy} onChange={handleKioskUpload} />
          </label>
        </div>

        {kioskThumbs.length > 0 && (
          <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap", marginTop: "var(--space-2)" }}>
            {kioskThumbs.map((t) => (
              <div
                key={t.name}
                style={{ position: "relative", width: 75, height: 75, border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-1)", cursor: "pointer", overflow: "hidden", background: "var(--color-bg)" }}
                onClick={() => setAsKioskIcon(t)}
              >
                <img src={t.url} alt={t.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeKioskThumb(t.name); }}
                  aria-label="Remove thumbnail"
                  title="Remove thumbnail"
                  style={imageRemoveBtnStyleSmall}
                >
                  <StatusIcon kind="cogDelete" size={12} style={{ color: "var(--color-error)" }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </SimpleFieldset>

      <hr style={{ border: "none", borderTop: "1px solid var(--color-border-default)", margin: "var(--space-3) 0" }} />

      <SimpleFieldset legend="App Image">
        <span className={styles.hint}>
          This image will be used in the Payment Application. Only one is allowed; uploading replaces the old one.
        </span>
        <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start", marginTop: "var(--space-2)" }}>
          <div style={{ width: 180, maxWidth: "100%", minHeight: 108, border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-1)", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--color-bg)", position: "relative" }}>
            {appIcon ? (
              <>
                <img src={appIcon.url} alt={appIcon.name} style={{ maxWidth: "100%", maxHeight: 108, objectFit: "contain" }} />
                <button
                  type="button"
                  onClick={removeAppIcon}
                  aria-label="Remove app image"
                  title="Remove app image"
                  style={imageRemoveBtnStyle}
                >
                  <StatusIcon kind="cogDelete" size={14} style={{ color: "var(--color-error)" }} />
                </button>
              </>
            ) : (
              <span style={{ color: "var(--color-text-muted)", fontSize: "var(--text-xs)" }}>No image</span>
            )}
          </div>
          <label className={styles.secondaryBtn} style={{ cursor: imageBusy ? "default" : "pointer", opacity: imageBusy ? 0.6 : 1 }}>
            {imageBusy ? "Uploading…" : "Add app image"}
            <FileInputSafe accept="image/png,image/gif,image/jpeg" hidden disabled={imageBusy} onChange={handleAppUpload} />
          </label>
        </div>
      </SimpleFieldset>
    </div>
  );

  // ── Tabs assembly ─────────────────────────────────────
  /** Badge on a tab label: the number chosen, or a word for "no restriction". */
  const countBadge = useCallback(
    (n: number, emptyWord: string) => (
      <span className={n > 0 ? styles.tabCountBadge : styles.tabCountBadgeMuted}>
        {n > 0 ? n : emptyWord}
      </span>
    ),
    [],
  );

  const tabs = useMemo(() => {
    const list: { key: string; label: React.ReactNode; children: React.ReactNode }[] = [
      { key: "deal", label: isIE ? "Info" : "Deal", children: dealTab },
    ];
    if (!isIE) {
      list.push({
        key: "locations",
        label: <>{tabLabel("Valid Locations", "locationAccess")}{countBadge(locationSelectedTotal, "all")}</>,
        children: locationsTab,
      });
      list.push({
        key: "products",
        label: <>{tabLabel("Products", "groups")}{countBadge(productSelectedTotal, "any")}</>,
        children: productsTab,
      });
      list.push({ key: "times", label: tabLabel("Valid Times", "validDays"), children: timesTab });
    }
    list.push({ key: "images", label: "Images", children: imagesTab });
    return list;
  }, [isIE, dealTab, locationsTab, productsTab, timesTab, imagesTab, tabLabel, countBadge,
      locationSelectedTotal, productSelectedTotal]);

  const title = isNew
    ? `Creating new ${modeLabel}`
    : `Editing ${modeLabel} #${dealId}`;

  return (
    <GridEditModal
      title={title}
      wide
      loading={loading}
      saving={saving}
      statusMessage={statusMessage}
      saveLabel={isNew ? "Create" : "Save"}
      onClose={onClose}
      onSave={handleSave}
    >
      {changed.size > 0 && (
        <div className={styles.changeNotice} role="status">
          <StatusIcon kind="info" size={16} style={{ color: "var(--color-primary)" }} />
          <span>
            Highlighted fields are the suggested changes — nothing is saved until you press Save.
          </span>
        </div>
      )}
      <SimpleTabs tabs={tabs} />
    </GridEditModal>
  );
}

/* ── CheckboxTree sub-component ──────────────────────────── */

function CheckboxTree({
  group,
  onToggle,
}: {
  group: TreeGroup;
  onToggle: (itemId: number, checked: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{ marginBottom: "var(--space-2)" }}>
      <div
        style={{ cursor: "pointer", fontWeight: "var(--font-semibold)", padding: "var(--space-1) 0", userSelect: "none" }}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "▾" : "▸"} {group.label} ({group.matchCount})
        {group.selectedCount > 0 && (
          <span className={styles.treeSelectedBadge}>{group.selectedCount} selected</span>
        )}
        {group.unavailableCount > 0 && (
          <span className={styles.treeUnavailableBadge}>{group.unavailableCount} not in your list</span>
        )}
        {group.tooltip && <span title={group.tooltip} style={{ marginLeft: "var(--space-1)", cursor: "help", color: "var(--color-text-muted)" }}>ⓘ</span>}
      </div>
      {expanded && (
        <div style={{ paddingLeft: "var(--space-4)", maxHeight: 300, overflowY: "auto", minWidth: 0 }}>
          {group.children.map((node) => (
            <label
              key={node.id}
              style={{ display: "flex", alignItems: "center", gap: "var(--space-1)", fontSize: "var(--text-sm)", padding: "1px 0", cursor: node.itemId === -1 ? "default" : "pointer", minWidth: 0 }}
            >
              {node.itemId !== -1 ? (
                <input
                  type="checkbox"
                  checked={node.checked}
                  onChange={(e) => onToggle(node.itemId, e.target.checked)}
                  style={{ flexShrink: 0 }}
                />
              ) : null}
              <span style={node.itemId === -1 ? { fontStyle: "italic", color: "var(--color-text-muted)", minWidth: 0, overflowWrap: "anywhere" } : { minWidth: 0, overflowWrap: "anywhere" }}>
                {node.label}
              </span>
              {node.unavailable && (
                <span className={styles.treeUnavailableTag}>not in your list</span>
              )}
            </label>
          ))}
          {group.unavailableCount > 0 && (
            <div className={styles.treeUnavailableNote}>
              Selected in this deal, but not in your list on this endpoint: they may belong to another
              operator, be outside your access, or no longer exist. Untick them unless you know they
              are right — they are saved as they are.
            </div>
          )}
          {group.omittedCount > 0 && (
            <div className={styles.treeOmitted}>
              {group.omittedCount} more not shown — narrow the filter to reach them.
              Everything selected is always listed above.
            </div>
          )}
          {group.children.length === 0 && (
            <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>No items</span>
          )}
        </div>
      )}
    </div>
  );
}
